/* eslint-disable no-unused-vars */
import { useEffect, useState, useCallback, Fragment } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import Divider from '@mui/material/Divider';
import { useNavigate } from 'react-router-dom';
import { Buttons, Loader } from 'components';
import { routes } from 'config';
import ExcludeCategories from './components/ExcludeCategories';
import ExcludeAdvertisers from './components/ExcludeAdvertisers';
import ExcludeCampaigns from './components/ExcludeCampaigns';
import ExcludeApps from './components/ExcludeApps';
import ExcludePackageName from './components/ExcludePackageName';
import ExcludePublishers from './components/ExcludePublishers';
import { useForm, Controller } from 'react-hook-form';
import * as S from './ExcludeSettingsForm.styled';
import { isAdvertiserRole } from 'store/selectors';
import { showPolipop } from 'utils';

let timer_id;

const fieldsComponentsMap = new Map([
  ['typeList', ExcludeCategories],
  ['excluded_advertisers', ExcludeAdvertisers],
  ['excluded_publishers', ExcludePublishers],
  ['excluded_campaigns', ExcludeCampaigns],
  ['excluded_apps', ExcludeApps],
  ['excluded_packages', ExcludePackageName],
]);

function isFieldArray(obj, fieldName, isAdvertiser) {
  if (!Array.isArray(obj[fieldName])) {
    return false;
  }

  if (fieldName === 'excluded_advertisers') return !isAdvertiser;
  if (fieldName === 'excluded_publishers') return isAdvertiser;

  return true;
}

const defaultFooterBtns = [
  {
    type: 'submit',
    id: 'saveButton',
    buttonColor: 'Primary',
    btnText: 'Save Settings',
    isDisabled: true,
    isRedirect: false,
    redirectId: '',
  },
  {
    type: 'submit',
    id: 'saveAndCloseButton',
    buttonColor: 'Secondary',
    btnText: 'Save & Close',
    isDisabled: true,
    isRedirect: true,
    redirectId: 'manageApps',
  },
  {
    type: 'btn',
    id: 'cancelButton',
    buttonColor: 'Secondary',
    btnText: 'Cancel',
    isDisabled: false,
    isRedirect: true,
    redirectId: 'manageApps',
  },
];

const ExcludeSettingsForm = ({ data, updateAction, result, footerBtnsObj = defaultFooterBtns }) => {
  const isAdvertiser = useSelector(isAdvertiserRole);
  const navigate = useNavigate();
  const { pba_id } = useParams();
  const [redirect, setRedirect] = useState(false);

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { isValid, isDirty },
  } = useForm({
    mode: 'onChange',
  });

  const onSubmit = async (data) => {
    let newDate = {
      ...data,
      excluded_packages: data?.excluded_packages?.map((pack) => pack.package_id) ?? [],
      excluded_advertisers: isAdvertiser
        ? data?.excluded_publishers?.map((pub) => pub.pb_number) ?? []
        : data?.excluded_advertisers?.map((adv) => adv.pb_number) ?? [],
      excluded_campaigns: data?.excluded_campaigns?.map((cam) => cam.cm_campaignid) ?? [],
      excluded_apps: data?.excluded_apps?.map((app) => app.pba_number) ?? [],
      excluded_types: data?.typeList?.filter((type) => type.selected).map((type) => type?.value),
    };
    if (pba_id) {
      newDate = { ...newDate, pba_id };
    }
    const res = await updateAction(newDate);
    return res;
  };

  const successAction = () => {};

  const errorAction = () => {
    reset(data);
  };

  useEffect(() => {
    showPolipop(result, successAction, errorAction);
    if (redirect && result.isSuccess) {
      timer_id = setTimeout(() => {
        redirectAction(redirect);
        reset(watch());
      }, 2500);
    }
    return () => {
      clearTimeout(timer_id);
    };
  }, [result]);

  const redirectAction = useCallback((redirectId) => {
    const link = routes.list.find((route) => route.id === redirectId).url;
    navigate(link);
  }, []);

  const onClickAction = useCallback((e, btnData) => {
    if (btnData.type === 'submit') {
      handleSubmit();
      btnData.isRedirect && setRedirect(btnData.redirectId);
      return;
    }
    if (btnData.isRedirect) {
      e.preventDefault();
      redirectAction(btnData.redirectId);
    }
  }, []);

  return (
    <S.Container>
      {result.isLoading && <Loader.Global delay={10} />}
      {data && (
        <S.Form onSubmit={handleSubmit(onSubmit)} id='excludeSettings'>
          <S.Box>
            <S.Head>Exclude Settings</S.Head>
            {Array.from(fieldsComponentsMap).map(([fieldName, Component], index) => {
              return (
                <Fragment key={fieldName}>
                  {isFieldArray(data, fieldName, isAdvertiser) && (
                    <>
                      <S.Field>
                        <Controller
                          defaultValue={data?.[fieldName]}
                          name={fieldName}
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <Component id={fieldName} value={value} onChange={onChange} />
                          )}
                        />
                      </S.Field>
                      {index !== fieldsComponentsMap.size - 1 && <Divider variant='fullWidth' />}
                    </>
                  )}
                </Fragment>
              );
            })}
          </S.Box>
          <S.BtnGroup>
            {footerBtnsObj.map((btnData, index) => {
              const ButtonComponent = Buttons[btnData.buttonColor + 'Normal'];
              return (
                <ButtonComponent
                  key={index}
                  type={btnData.type}
                  isDisabled={btnData.isDisabled ? !isValid || !isDirty : false}
                  id={btnData.id}
                  onClick={(e) => onClickAction(e, btnData)}
                >
                  {btnData.btnText}
                </ButtonComponent>
              );
            })}
          </S.BtnGroup>
        </S.Form>
      )}
    </S.Container>
  );
};

export default ExcludeSettingsForm;

ExcludeSettingsForm.ExcludeCategories = ExcludeCategories;
ExcludeSettingsForm.ExcludeAdvertisers = ExcludeAdvertisers;
ExcludeSettingsForm.ExcludePublishers = ExcludePublishers;
ExcludeSettingsForm.ExcludeCampaigns = ExcludeCampaigns;
ExcludeSettingsForm.ExcludeApps = ExcludeApps;
ExcludeSettingsForm.ExcludePackageName = ExcludePackageName;
ExcludeSettingsForm.S = S;
